// src/pages/misc/landing/LandingVideoModal.tsx
// [HANDOFF] All five clips are placeholders today (`src: null`): the popup then shows "Video en camino · <duration>". Fill VIDEOS[key].src
//   with an mp4 URL and the same popup plays it (autoplay, controls). Keys: hero, c1, ia, money, call (called from LandingPage.tsx).
// [ANIMATION] Opens as a centered dialog over a dark backdrop (.vl-vid-backdrop in landing.css). Esc or a backdrop click closes it, the page
//   scroll is locked while open and restored on close. It renders into document.body via a portal so no section can clip it.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, X } from 'lucide-react';

/**
 * Small "more info" video popup used across the landing.
 * One component, many triggers: each trigger passes a `LandingVideo` describing
 * which clip to show. Drop a real `src` in the VIDEOS table below when the clips exist.
 *
 * [UI-BACKEND-TODO] Replace `src: null` with the real mp4/embed per key.
 */
export type LandingVideoKey = 'hero' | 'c1' | 'ia' | 'money' | 'call';

export interface LandingVideo {
  key: LandingVideoKey;
  title: string;
  note: string;
  duration: string;
  src: string | null;
}

export const VIDEOS: Record<LandingVideoKey, LandingVideo> = {
  hero: {
    key: 'hero',
    title: 'Mira cómo funciona',
    note: 'Un recorrido corto por Ventaje, de lead a cierre.',
    duration: '2:00',
    src: null,
  },
  c1: {
    key: 'c1',
    title: 'Tú vendes, Ventaje documenta',
    note: 'Cómo se llena la ficha sola mientras tu equipo atiende.',
    duration: '0:45',
    src: null,
  },
  ia: {
    key: 'ia',
    title: 'La IA adentro del lead',
    note: 'Qué hace la IA en cada lead y cómo le subes o bajas el nivel.',
    duration: '0:45',
    src: null,
  },
  money: {
    key: 'money',
    title: 'La IA da el seguimiento',
    note: 'Cómo un solo cerrador cubre lo que antes hacían diez.',
    duration: '0:45',
    src: null,
  },
  call: {
    key: 'call',
    title: 'La IA llamando en vivo',
    note: 'Una llamada real, con tu voz, de principio a fin.',
    duration: '0:30',
    src: null,
  },
};

export const LandingVideoModal: React.FC<{
  video: LandingVideo | null;
  onClose: () => void;
}> = ({ video, onClose }) => {
  useEffect(() => {
    if (!video) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [video, onClose]);

  if (!video) return null;

  return createPortal(
    <div className="vl-vid-backdrop" onClick={onClose} role="presentation">
      <div
        className="vl-vid"
        role="dialog"
        aria-modal="true"
        aria-label={video.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="vl-vid-close" aria-label="Cerrar" onClick={onClose}>
          <X size={18} />
        </button>
        <div className="vl-vid-frame">
          {video.src ? (
            <video src={video.src} controls autoPlay playsInline />
          ) : (
            <div className="vl-vid-empty">
              <span className="vl-vid-play">
                <Play size={22} fill="currentColor" />
              </span>
              <small>Video en camino · {video.duration}</small>
            </div>
          )}
        </div>
        <div className="vl-vid-meta">
          <span className="vl-tag">{video.duration}</span>
          <h3>{video.title}</h3>
          <p>{video.note}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
};
